import _ from 'lodash';
import { useState } from 'react';
import {
  Checkbox,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

import { Form, SectionWrapper, WrapperSelect } from 'common/components';
import { AddNewItem, UpdateValueButton } from './DrugFactsButton';

import { useFactPanelQasRequiredFields } from '../../../hook/useFactPanelQasRequiredFields';

import './ActiveIngredients.less';
import { useGetNutritionEnum } from '../../../hook/useNutritionLabel';
import { sortBy } from 'lodash';
import {
  QaSpecHelpContentLabel,
  QaSpecHelpContentWithoutLabel,
} from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import { TOOlTIP_FIELDS } from 'static/tooltipFields';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { getFormattedPath } from 'pages/qa-spec/components/shared/QaSpecHelper';

const ACTIVE_INGREDIENTS_FIELD_NAME = [
  'ingredientName',
  'purposeDescriptor',
  'quantity',
  'quantityUom',
  'italicName',
  'italicPurposeDescriptor',
  'quantityPrecision',
];

const ActiveIngredients = (props) => {
  const form = Form.useFormInstance();

  const { productId, typeView } = props;

  const [mode, setMode] = useState();

  const activeIngredientsList = Form.useWatch('activeIngredients', form);

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.drugFactsLabel',
  });

  const onClickAddNew = () => {
    form.resetFields(ACTIVE_INGREDIENTS_FIELD_NAME);
    form.setFieldsValue({
      quantityPrecision: 'Exact',
    });
    setMode('add');
  };

  const onClickCancel = () => {
    setMode('view');
  };

  const onClickAdd = () => {
    form
      .validateFields(ACTIVE_INGREDIENTS_FIELD_NAME)
      .then((value) => {
        form.setFieldsValue({
          activeIngredients: [...(activeIngredientsList || []), value],
        });
        setMode('view');
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <div style={{ padding: '8px 0px', minHeight: '47vh' }}>
      <Row>
        <Col span={24}>
          <Form.Item
            name='dosageUnit'
            label={
              <TraceableCommentContainer
                qaSpecName={getFormattedPath(
                  TOOlTIP_FIELDS.drugFactDosageUnit.fieldPath
                )}
                qaSpecFieldName='Dosage Unit'
              >
                <QaSpecHelpContentLabel
                  fieldPath={TOOlTIP_FIELDS.drugFactDosageUnit.fieldPath}
                  isEnabled={typeView === 'qa-spec'}
                >
                  Dosage Unit
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: ['activeIngredients', 'dosageUnit'],
                displayName: 'Dosage Unit',
              }),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Divider />
      <ActiveIngredientsList
        setMode={setMode}
        mode={mode}
        productId={productId}
        typeView={typeView}
      />
      {mode === 'add' && (
        <AddIngredientsForm productId={productId} typeView={typeView} />
      )}
      <AddNewItem
        mode={mode}
        onClickAdd={onClickAdd}
        onClickCancel={onClickCancel}
        onClickAddNew={onClickAddNew}
      />
    </div>
  );
};

export default ActiveIngredients;

const ActiveIngredientsList = ({ setMode, mode, productId, typeView }) => {
  const form = Form.useFormInstance();
  const activeIngredientsList = Form.useWatch('activeIngredients', form);

  const onClickUpdate = (index) => {
    const cloneActiveIngredientsList = _.cloneDeep(activeIngredientsList);

    form
      .validateFields(ACTIVE_INGREDIENTS_FIELD_NAME)
      .then((value) => {
        cloneActiveIngredientsList[index] = value;

        form.setFieldsValue({
          activeIngredients: cloneActiveIngredientsList,
        });
        setMode('view');
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onClickDelete = (index) => {
    const cloneActiveIngredientsList = _.cloneDeep(activeIngredientsList);
    cloneActiveIngredientsList.splice(index, 1);

    form.setFieldsValue({
      activeIngredients: cloneActiveIngredientsList,
    });
  };

  const onClickEdit = (index, item) => {
    setMode(`edit-${index}`);
    form.setFieldsValue({ ...item });
  };

  const onCancel = () => {
    setMode('view');
  };

  return (
    <>
      <Form.Item name='activeIngredients' hidden />
      {activeIngredientsList?.map((item, index) => {
        const {
          ingredientName,
          quantity,
          quantityUom,
          purposeDescriptor,
          quantityPrecision,
          italicName,
          italicPurposeDescriptor,
        } = item;

        const isEdit = mode === `edit-${index}`;
        return (
          <div key={index}>
            {isEdit ? (
              <>
                <AddIngredientsForm productId={productId} typeView={typeView} />
                <UpdateValueButton
                  onCancel={onCancel}
                  onClickUpdate={() => onClickUpdate(index)}
                />
              </>
            ) : (
              <SectionWrapper
                title={
                  <Space>
                    <EditOutlined onClick={() => onClickEdit(index, item)} />
                    <DeleteOutlined onClick={() => onClickDelete(index)} />
                  </Space>
                }
                placement='right'
                style={{ width: '100%', maxWidth: '100%' }}
              >
                <Row wrap={false}>
                  <Col style={{ maxWidth: '48%' }}>
                    <Typography.Text italic={italicName}>
                      {ingredientName}
                      {(quantity || quantityUom) && (
                        <span>
                          (
                          <span>
                            {quantity}
                            {quantityUom}
                          </span>
                          )
                        </span>
                      )}
                    </Typography.Text>
                  </Col>

                  <Col flex={1}></Col>

                  <Col style={{ maxWidth: '48%' }}>
                    <Typography.Text
                      italic={italicPurposeDescriptor}
                      style={{ textAlign: 'right', display: 'block' }}
                    >
                      {purposeDescriptor}
                    </Typography.Text>
                  </Col>
                </Row>
                <Row>
                  <Col flex={18}>
                    <span>{quantityPrecision}</span>
                  </Col>
                </Row>
              </SectionWrapper>
            )}
          </div>
        );
      })}
    </>
  );
};

const AddIngredientsForm = (props) => {
  const form = Form.useFormInstance();

  const { productId, typeView } = props;

  const activeIngredientsList = Form.useWatch('activeIngredients', form);
  const { measurementPrecisionCodeEnum } = useGetNutritionEnum(); // Fetch precision options

  const { getRequiredRule } = useFactPanelQasRequiredFields({
    productId,
    typeView,
    section: 'qaSpecification.factsPanel.drugFactsLabel',
  });

  const isQaSpecType = typeView === 'qa-spec';

  return (
    <div className='add-directions-form'>
      {activeIngredientsList?.length > 0 && <Divider />}
      <Row gutter={4}>
        <Col span={24}>
          <Form.Item
            name='ingredientName'
            rules={[{ required: true, message: 'Ingredient Name is required' }]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.activeIngredientName.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Input placeholder='Ingredient Name' />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name='purposeDescriptor'
            rules={[
              getRequiredRule({
                fieldName: [
                  'activeIngredients',
                  'activeIngredientsDetail',
                  'purposeDescriptor',
                ],
                displayName: 'Purpose',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={
                TOOlTIP_FIELDS.activeIngredientPurposeDescriptor.fieldPath
              }
              isEnabled={isQaSpecType}
            >
              <Input placeholder='Purpose' />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={24}>
          {/* New Quantity Precision Dropdown */}
          <Form.Item
            name='quantityPrecision'
            initialValue='Exact' // Default to "Exact"
            rules={[
              getRequiredRule({
                fieldName: ['activeIngredients', 'quantityPrecision'],
                displayName: 'Quantity Precision',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={
                TOOlTIP_FIELDS.activeIngredientQuantityPrecision.fieldPath
              }
              isEnabled={isQaSpecType}
            >
              <WrapperSelect
                placeholder='Select Quantity Precision'
                showSearch
                filterOption={(input, option) =>
                  option?.value?.toLowerCase().indexOf(input?.toLowerCase()) >=
                  0
                }
                getPopupContainer={(triggerNode) => triggerNode}
              >
                {sortBy(measurementPrecisionCodeEnum?.[0]?.enumProperties, [
                  'enumDisplayName',
                  'enumGroup',
                ])?.map((item, index) => (
                  <Select.Option value={item.enumDisplayName} key={index}>
                    {item.enumDisplayName}
                  </Select.Option>
                ))}
              </WrapperSelect>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            name='quantity'
            rules={[
              getRequiredRule({
                fieldName: [
                  'activeIngredients',
                  'activeIngredientsDetail',
                  'quantity',
                ],
                displayName: 'Quantity',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.activeIngredientQuantity.fieldPath}
              isEnabled={isQaSpecType}
            >
              <InputNumber style={{ width: '100%' }} placeholder='Quantity' />
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name='quantityUom'
            rules={[
              getRequiredRule({
                fieldName: [
                  'activeIngredients',
                  'activeIngredientsDetail',
                  'quantityUom',
                ],
                displayName: 'Unit',
              }),
            ]}
          >
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.activeIngredientQuantityUom.fieldPath}
              isEnabled={isQaSpecType}
            >
              <WrapperSelect
                placeholder='Unit'
                getPopupContainer={(triggerNode) => {
                  return triggerNode;
                }}
                allowClear={true}
              >
                <Select.Option value='g'>g</Select.Option>
                <Select.Option value='mg'>mg</Select.Option>
                <Select.Option value='mcg'>mcg</Select.Option>
              </WrapperSelect>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='italicName' valuePropName='checked'>
            <QaSpecHelpContentWithoutLabel
              fieldPath={TOOlTIP_FIELDS.activeIngredientItalicName.fieldPath}
              isEnabled={isQaSpecType}
            >
              <Checkbox>Italic Name</Checkbox>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='italicPurposeDescriptor' valuePropName='checked'>
            <QaSpecHelpContentWithoutLabel
              fieldPath={
                TOOlTIP_FIELDS.activeIngredientItalicPurposeDescriptor.fieldPath
              }
              isEnabled={isQaSpecType}
            >
              <Checkbox>Italic Purpose</Checkbox>
            </QaSpecHelpContentWithoutLabel>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};
